'use client';

import dynamic from 'next/dynamic';
const CompletionOptions = dynamic(() => import('./CompletionOptions'));
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { ExclamationTriangleIcon, CheckCircleIcon, ChevronDownIcon, SparklesIcon } from '@heroicons/react/24/outline';
import type { Semester, SemesterItem } from '@/app/types/interfaces';
import { normalize } from '@/app/services/degreeRequirementService';
import { useCatalogCourses } from '@/app/services/useCatalogCourses';
import { PlannerContext, userListMappingName, type CompletedMode, type PlanIssue, type PlannerContextValue, type PlannerOverlay, type UserCourseList } from './plannerContext';
import { diffProposal, proposalCounts, type ProposalTerm, type ProposedTermInput } from './planProposal';
import { buildCatalogIndex, buildPlacement, buildPrograms, buildTerms, displayTitle, formatCode, programColor } from './plannerModel';
import { PlannerOverlayHost } from './PlannerOverlays';
import SemesterView from './views/SemesterView';
import DegreeView from './views/DegreeView';
import CoursesView from './views/CoursesView';
import GraphView from './views/GraphView';
import ListView from './views/ListView';
import FlowsheetView from './views/FlowsheetView';

export type PlannerView = 'semesters' | 'degree' | 'courses' | 'graph' | 'list' | 'flowsheet';

const VIEWS: Array<{ id: PlannerView; label: string; hint: string }> = [
  { id: 'semesters', label: 'Semesters', hint: 'Terms side by side with what’s left' },
  { id: 'flowsheet', label: 'Flowsheet', hint: 'Your semester-by-semester prerequisite sequence' },
  { id: 'degree', label: 'Degree', hint: 'Every requirement as slots' },
  { id: 'courses', label: 'Courses', hint: 'The classes you’ll take' },
  { id: 'graph', label: 'Graph', hint: 'How courses unlock each other' },
  { id: 'list', label: 'List', hint: 'One sortable table' },
];
/** The three everyday views get tabs; the rest wait behind "More views". */
const PRIMARY_VIEWS = new Set<PlannerView>(['semesters', 'flowsheet', 'degree']);
const VIEW_STORAGE_KEY = 'scarlet-sync:planner-view';
const COMPLETED_STORAGE_KEY = 'scarlet-sync:planner-completed';
const COMPLETED_MODES: Array<[CompletedMode, string]> = [['show', 'Show'], ['compact', 'Compact'], ['hide', 'Hide']];
/** Views that can narrow to one program. */
const SCOPED_VIEWS = new Set<PlannerView>(['flowsheet', 'degree']);

export interface PlannerWorkspaceProps {
  /** Open the transfer review when Plan's dedicated transfer view is selected. */
  openTransferOptions?: boolean;
  /** Account + plan scoped browser notes; deliberately separate from academic history. */
  completionStorageKey?: string;
  semesters: Semester[];
  selectedDegreeRequirements: Array<{ degreeId: number; degreeName: string; requirements?: unknown[]; coverageUnavailable?: boolean }>;
  degreeProgress: Map<number, { requirementsProgress?: Array<Record<string, unknown>> }> | null;
  expandedPools: Map<string, string[]>;
  degreeCourseListsDirect: Map<string, { classes: string[]; name?: string; description?: string }>;
  userPrereqInfo: Array<{ course_code?: string | number }>;
  creditsMap: Map<string, number>;
  earnedCredits: number;
  issues: PlanIssue[];
  getCourseName: (code: string) => string;
  onAddCourseByCodeToSemester: (semesterNumber: number, code: string, name: string, credits: number) => void;
  onAddCourseListByNameToSemester: (semesterNumber: number, mappingName: string, label: string, extra?: { courseListId?: number; credits?: number }) => void;
  onDropCourse: (toSemesterNumber: number, fromSemesterNumber: number, index: number) => Promise<void> | void;
  onRemoveSemesterItem: (semesterNumber: number, index: number) => void;
  onCourseListClick: (item: SemesterItem, semesterNumber: number, index: number) => void;
  onAddFromSearch: (semesterNumber: number) => void;
  onAddSemester: () => void;
  onDeleteSemester: (semesterNumber: number) => void;
  onDegreeImport: () => void;
  onAskCourseAI: (intent: 'explain', courseCode: string, placement?: { semesterName?: string | null }) => void;
  /** Raw auto-fill output awaiting review. It's re-diffed against the plan on every render. */
  proposedTerms?: ProposedTermInput[] | null;
  proposalBusy?: boolean;
  /** Merge accepted terms through the normal save path. */
  onAcceptProposal?: (proposal: ProposalTerm[], accept: 'all' | string[]) => Promise<void> | void;
  onDiscardProposal?: () => void;
  userCourseLists?: UserCourseList[];
  /** null while loading, false when the lists can't be shown. */
  userCourseListsReady?: boolean | null;
}

export default function PlannerWorkspace(props: PlannerWorkspaceProps) {
  const {
    semesters, selectedDegreeRequirements, degreeProgress, expandedPools, degreeCourseListsDirect, userPrereqInfo,
    creditsMap, earnedCredits, issues, getCourseName, proposedTerms, proposalBusy = false, userCourseLists, userCourseListsReady = false,
  } = props;
  const userLists = useMemo(() => userCourseLists || [], [userCourseLists]);
  const catalogCourses = useCatalogCourses();
  const [view, setView] = useState<PlannerView>('semesters');
  const [overlay, setOverlay] = useState<PlannerOverlay | null>(null);
  const [focusRequirementKey, setFocusRequirementKey] = useState<string | null>(null);
  const [completedMode, setCompletedMode] = useState<CompletedMode>('show');
  const [programScope, setProgramScope] = useState<number | null>(null);
  const completionLauncherRef = useRef<HTMLButtonElement>(null);
  const closeCompletion = useCallback(() => {
    setOverlay(null);
    // Lazy loading and transitions from a slot overlay can lose the original
    // focused element. Return to the stable toolbar command after unmount.
    window.requestAnimationFrame(() => completionLauncherRef.current?.focus({ preventScroll: true }));
  }, []);



  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(VIEW_STORAGE_KEY) as PlannerView | null;
      if (saved && VIEWS.some((v) => v.id === saved)) setView(saved);
      const savedCompleted = window.localStorage.getItem(COMPLETED_STORAGE_KEY);
      if (savedCompleted === 'show' || savedCompleted === 'compact' || savedCompleted === 'hide') setCompletedMode(savedCompleted);
    } catch { /* storage unavailable */ }
  }, []);
  const chooseView = (next: PlannerView) => {
    setView(next);
    setOverlay(null);
    try { window.localStorage.setItem(VIEW_STORAGE_KEY, next); } catch { /* storage unavailable */ }
  };

  const chooseCompletedMode = (next: CompletedMode) => {
    setCompletedMode(next);
    try { window.localStorage.setItem(COMPLETED_STORAGE_KEY, next); } catch { /* storage unavailable */ }
  };

  const index = useMemo(() => buildCatalogIndex(catalogCourses), [catalogCourses]);

  const userListsByMapping = useMemo(() => new Map(userLists.map((list) => [userListMappingName(list.id), list] as const)), [userLists]);
  const listCredits = useCallback((name: string): number => {
    const credits = (degreeCourseListsDirect.get(name)?.classes || userListsByMapping.get(name)?.classes || [])
      .map((code) => creditsMap.get(normalize(code)))
      .filter((c): c is number => typeof c === 'number' && c > 0)
      .sort((a, b) => a - b);
    return credits.length ? credits[Math.floor(credits.length / 2)] : 3;
  }, [degreeCourseListsDirect, userListsByMapping, creditsMap]);
  const listLabel = useCallback(
    (name: string) => degreeCourseListsDirect.get(name)?.name || userListsByMapping.get(name)?.name || name.replace(/_/g, ' '),
    [degreeCourseListsDirect, userListsByMapping],
  );

  const nameOf = useCallback((code: string): string => {
    const norm = normalize(code);
    const fromWorkspace = getCourseName(norm);
    if (fromWorkspace && normalize(fromWorkspace) !== norm) return displayTitle(fromWorkspace);
    const course = index.byCode.get(norm);
    return displayTitle(course?.fullTitle || course?.courseName || '') || formatCode(norm);
  }, [getCourseName, index]);
  const creditsOf = useCallback((code: string) => creditsMap.get(normalize(code)) || Number(index.byCode.get(normalize(code))?.credits) || 0, [creditsMap, index]);

  const terms = useMemo(() => buildTerms(semesters, creditsMap, listCredits), [semesters, creditsMap, listCredits]);
  const placement = useMemo(() => buildPlacement(terms), [terms]);
  const completed = useMemo(() => new Set(userPrereqInfo.map((u) => normalize(String(u.course_code ?? ''))).filter(Boolean)), [userPrereqInfo]);
  const programs = useMemo(
    () => buildPrograms({ selectedDegreeRequirements, degreeProgress, expandedPools, completed, placement, terms }),
    [selectedDegreeRequirements, degreeProgress, expandedPools, completed, placement, terms],
  );
  const requirementByKey = useMemo(() => new Map(programs.flatMap((p) => p.requirements.map((r) => [r.key, r] as const))), [programs]);
  const issuesByCode = useMemo(() => {
    const map = new Map<string, PlanIssue[]>();
    issues.forEach((issue) => {
      const code = normalize(issue.course);
      map.set(code, [...(map.get(code) || []), issue]);
    });
    return map;
  }, [issues]);

  const proposal = useMemo(() => (proposedTerms?.length ? diffProposal(semesters, proposedTerms, completed) : []), [proposedTerms, semesters, completed]);
  const hasProposal = proposal.length > 0;
  const { onDiscardProposal } = props;
  // Once every proposed item is in the plan (accepted, or added by hand) the proposal is done.
  useEffect(() => {
    if (proposedTerms?.length && !hasProposal && !proposalBusy) onDiscardProposal?.();
  }, [proposedTerms, hasProposal, proposalBusy, onDiscardProposal]);
  // A new proposal is reviewed on the term board, so bring it into view.
  const hadProposal = useRef(false);
  useEffect(() => {
    if (hasProposal && !hadProposal.current) { setView('semesters'); setOverlay(null); }
    hadProposal.current = hasProposal;
  }, [hasProposal]);

  const close = useCallback(() => setOverlay(null), []);
  const selectedCode = overlay?.kind === 'course' ? normalize(overlay.code) : null;

  const actions = useMemo<PlannerContextValue['actions']>(() => ({
    addCourse: (semesterNumber, code) => props.onAddCourseByCodeToSemester(semesterNumber, formatCode(code), nameOf(code), creditsOf(code)),
    addList: (semesterNumber, listName, label) => props.onAddCourseListByNameToSemester(semesterNumber, listName, label),
    moveItem: (from, itemIndex, to) => { void props.onDropCourse(to, from, itemIndex); },
    removeItem: (semesterNumber, itemIndex) => props.onRemoveSemesterItem(semesterNumber, itemIndex),
    addTerm: () => props.onAddSemester(),
    removeTerm: (semesterNumber) => props.onDeleteSemester(semesterNumber),
    addFromSearch: (semesterNumber) => props.onAddFromSearch(semesterNumber),
    chooseListCourse: (item, semesterNumber, itemIndex) => props.onCourseListClick(item, semesterNumber, itemIndex),
    askAboutCourse: (code, termName) => props.onAskCourseAI('explain', formatCode(code), { semesterName: termName ?? null }),
    importDegree: () => props.onDegreeImport(),
    addUserListPlaceholder: (semesterNumber, list) => {
      const numericId = Number(list.id);
      props.onAddCourseListByNameToSemester(semesterNumber, userListMappingName(list.id), list.name, {
        courseListId: Number.isFinite(numericId) ? numericId : undefined,
        credits: listCredits(userListMappingName(list.id)),
      });
    },
    acceptProposal: (accept) => { void props.onAcceptProposal?.(proposal, accept); },
    discardProposal: () => props.onDiscardProposal?.(),
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }), [props.onAddCourseByCodeToSemester, props.onAddCourseListByNameToSemester, props.onDropCourse, props.onRemoveSemesterItem, props.onAddSemester, props.onDeleteSemester, props.onAddFromSearch, props.onCourseListClick, props.onAskCourseAI, props.onDegreeImport, props.onAcceptProposal, props.onDiscardProposal, proposal, nameOf, creditsOf, listCredits]);

  const value: PlannerContextValue = {
    terms, programs, requirementByKey, placement, completed, index, issues, issuesByCode, earnedCredits,
    nameOf, creditsOf, listLabel, listCredits, selectedCode, focusRequirementKey, setFocusRequirementKey,
    open: setOverlay, close, actions, proposal, proposalBusy, userLists, userListsReady: userCourseListsReady,
    completedMode,
    programScope: programs.some((program) => program.degreeId === programScope) ? programScope : null,
  };
  const counts = proposalCounts(proposal);

  const activeHint = VIEWS.find((v) => v.id === view)?.hint;

  if (props.openTransferOptions) return <PlannerContext.Provider value={value}><CompletionOptions inline onClose={closeCompletion} storageKey={props.completionStorageKey} /></PlannerContext.Provider>;

  return (
    <PlannerContext.Provider value={value}>
      <div className="flex flex-col gap-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="flex w-full items-center gap-3 md:w-auto md:max-w-full">
            {/* On phones the switcher spans the row so every view fits without sideways scrolling. */}
            <div className="grid w-full grid-cols-4 rounded-xl bg-[var(--ss-surface-subtle)] p-1 ring-1 ring-inset ring-[var(--ss-border)] md:inline-flex md:w-auto md:flex-none" role="tablist" aria-label="Plan views">
              {VIEWS.filter((v) => PRIMARY_VIEWS.has(v.id)).map((v) => (
                <button
                  key={v.id}
                  type="button"
                  role="tab"
                  id={`planner-tab-${v.id}`}
                  aria-selected={view === v.id}
                  aria-controls="planner-view-panel"
                  title={v.hint}
                  onClick={() => chooseView(v.id)}
                  className={`min-h-12 flex-1 whitespace-nowrap rounded-lg px-1 md:min-h-9 md:flex-none md:px-3.5 text-[13px] font-semibold transition-colors motion-reduce:transition-none ${view === v.id ? 'bg-[var(--ss-surface)] text-[var(--ss-text)] shadow-e1' : 'text-[var(--ss-text-muted)] hover:text-[var(--ss-text)]'}`}
                >
                  {v.label}
                </button>
              ))}
              {(() => {
                const secondary = VIEWS.filter((v) => !PRIMARY_VIEWS.has(v.id));
                const activeSecondary = secondary.find((v) => v.id === view);
                return (
                  <details className="group relative" onKeyDown={(event) => { if (event.key === 'Escape') (event.currentTarget as HTMLDetailsElement).open = false; }}>
                    <summary
                      role="tab"
                      id={activeSecondary ? `planner-tab-${activeSecondary.id}` : undefined}
                      aria-selected={Boolean(activeSecondary)}
                      className={`flex min-h-12 cursor-pointer list-none items-center justify-center gap-1 whitespace-nowrap rounded-lg px-2 text-[13px] font-semibold md:min-h-9 md:px-3 [&::-webkit-details-marker]:hidden ${activeSecondary ? 'bg-[var(--ss-surface)] text-[var(--ss-text)] shadow-e1' : 'text-[var(--ss-text-muted)] hover:text-[var(--ss-text)]'}`}
                    >
                      {activeSecondary?.label ?? 'More views'}<ChevronDownIcon className="h-3.5 w-3.5 transition-transform group-open:rotate-180 motion-reduce:transition-none" aria-hidden="true" />
                    </summary>
                    <div role="menu" aria-label="More plan views" className="absolute right-0 z-30 mt-2 w-60 rounded-xl border border-[var(--ss-border)] bg-[var(--ss-surface)] p-1 shadow-e2 md:left-0 md:right-auto">
                      {secondary.map((v) => (
                        <button key={v.id} type="button" role="menuitemradio" aria-checked={view === v.id}
                          onClick={(event) => { chooseView(v.id); (event.currentTarget.closest('details') as HTMLDetailsElement | null)?.removeAttribute('open'); }}
                          className={`flex min-h-12 w-full flex-col items-start rounded-lg px-3 py-1.5 text-left md:min-h-0 ${view === v.id ? 'bg-[var(--ss-surface-subtle)]' : 'hover:bg-[var(--ss-surface-subtle)]'}`}>
                          <span className="text-[13px] font-semibold text-[var(--ss-text)]">{v.label}</span>
                          <span className="text-xs text-[var(--ss-text-muted)]">{v.hint}</span>
                        </button>
                      ))}
                    </div>
                  </details>
                );
              })()}
            </div>
            <span className="hidden whitespace-nowrap text-xs text-[var(--ss-text-muted)] xl:inline">{activeHint}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2">
          <button ref={completionLauncherRef} type="button" onClick={() => setOverlay({ kind: 'completion' })} className="min-h-12 rounded-lg border border-[var(--ss-border)] px-3 text-[13px] font-semibold text-[var(--ss-text)] hover:bg-[var(--ss-surface-subtle)] md:min-h-9">Completion options</button>
          <button
            type="button"
            onClick={(e) => setOverlay(overlay?.kind === 'issues' ? null : { kind: 'issues', anchor: e.currentTarget })}
            aria-expanded={overlay?.kind === 'issues'}
            className={`inline-flex min-h-12 items-center md:min-h-9 gap-1.5 rounded-lg border px-3 text-[13px] font-semibold ${issues.length ? 'border-[color-mix(in_srgb,var(--ss-critical)_35%,var(--ss-border))] bg-[var(--ss-critical-bg)] text-[var(--ss-critical)]' : 'border-[var(--ss-border)] text-[var(--ss-text-muted)]'}`}
          >
            {issues.length ? <ExclamationTriangleIcon className="h-4 w-4" aria-hidden="true" /> : <CheckCircleIcon className="h-4 w-4" aria-hidden="true" />}
            {issues.length ? `${issues.length} issue${issues.length === 1 ? '' : 's'}` : 'No issues'}
          </button>
          </div>
        </div>
        <div className="flex flex-wrap items-center gap-x-5 gap-y-2 text-[12.5px]">
          {programs.length > 1 && SCOPED_VIEWS.has(view) ? (
            <div role="radiogroup" aria-label="Program shown" className="flex flex-wrap items-center gap-1.5">
              <span className="font-semibold text-[var(--ss-text-muted)]">Program</span>
              {[{ id: null as number | null, label: 'Entire plan', color: null as string | null }, ...programs.map((program) => ({ id: program.degreeId as number | null, label: program.degreeName, color: programColor(program.programIndex) }))].map((option) => {
                const active = value.programScope === option.id;
                return <button key={option.id ?? 'all'} type="button" role="radio" aria-checked={active} onClick={() => setProgramScope(option.id)}
                  className={`inline-flex min-h-12 max-w-[16rem] items-center gap-1.5 rounded-full border px-3 font-semibold md:min-h-8 ${active ? 'border-[var(--ss-text)] bg-[var(--ss-surface)] text-[var(--ss-text)]' : 'border-[var(--ss-border)] text-[var(--ss-text-muted)] hover:text-[var(--ss-text)]'}`}>
                  {option.color ? <span aria-hidden className="h-2 w-2 shrink-0 rounded-full" style={{ backgroundColor: option.color }} /> : null}
                  <span className="truncate">{option.label}</span>
                </button>;
              })}
            </div>
          ) : null}
          {view === 'semesters' || SCOPED_VIEWS.has(view) ? (
            <div role="radiogroup" aria-label="Completed coursework" className="flex items-center gap-1.5">
              <span className="font-semibold text-[var(--ss-text-muted)]">Completed</span>
              <span className="inline-flex rounded-lg bg-[var(--ss-surface-subtle)] p-0.5 ring-1 ring-inset ring-[var(--ss-border)]">
                {COMPLETED_MODES.map(([mode, label]) => <button key={mode} type="button" role="radio" aria-checked={completedMode === mode} onClick={() => chooseCompletedMode(mode)}
                  className={`min-h-12 rounded-md px-2.5 font-semibold md:min-h-7 ${completedMode === mode ? 'bg-[var(--ss-surface)] text-[var(--ss-text)] shadow-e1' : 'text-[var(--ss-text-muted)] hover:text-[var(--ss-text)]'}`}>{label}</button>)}
              </span>
            </div>
          ) : null}
        </div>
        {hasProposal ? (
          <section aria-label="Auto-fill proposal" className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-dashed border-[var(--ss-brand)] bg-[var(--ss-brand-bg)] px-4 py-3">
            <p className="flex min-w-0 items-start gap-2 text-[13px]">
              <SparklesIcon className="mt-0.5 h-4 w-4 flex-none text-[var(--ss-brand)]" aria-hidden="true" />
              <span>
                <b>Auto-fill suggests {counts.items} item{counts.items === 1 ? '' : 's'} across {counts.terms} term{counts.terms === 1 ? '' : 's'}</b>
                <span className="text-[var(--ss-text-muted)]"> · about {counts.credits} cr. Shown as dashed cards. Nothing is saved until you accept, and nothing in your plan is replaced.</span>
              </span>
            </p>
            <span className="flex flex-none items-center gap-2">
              <button type="button" disabled={proposalBusy} onClick={() => actions.discardProposal()} className="min-h-12 rounded-lg px-3 text-[13px] font-semibold text-[var(--ss-text-muted)] md:min-h-9 hover:text-[var(--ss-text)] disabled:opacity-50">Discard</button>
              <button type="button" disabled={proposalBusy} aria-busy={proposalBusy} onClick={() => actions.acceptProposal('all')} className="min-h-12 rounded-lg bg-[var(--ss-brand)] px-3.5 md:min-h-9 text-[13px] font-semibold text-[var(--ss-brand-contrast)] hover:bg-[var(--ss-brand-strong)] disabled:opacity-50">{proposalBusy ? 'Saving…' : 'Accept all'}</button>
            </span>
          </section>
        ) : null}
        <div id="planner-view-panel" role="tabpanel" aria-labelledby={`planner-tab-${view}`}>
          {view === 'semesters' ? <SemesterView /> : null}
          {view === 'degree' ? <DegreeView /> : null}
          {view === 'courses' ? <CoursesView /> : null}
          {view === 'flowsheet' ? <FlowsheetView /> : null}
          {view === 'graph' ? <GraphView /> : null}
          {view === 'list' ? <ListView /> : null}
        </div>
      </div>
      <PlannerOverlayHost overlay={overlay} completionStorageKey={props.completionStorageKey} onCompletionClose={closeCompletion} />
    </PlannerContext.Provider>
  );
}
